/* ══════════════════════════════════════════════════════════════════
   表单套件的**配置件**。所有表单（联系 / 门店 / 达人 / 保修 / 试骑）
   共用这一份。

   它存在的唯一理由：**让主题里不出现任何店铺相关的值。**

   以前每张表单的 section 设置里都要填提交地址、上传地址、Turnstile
   sitekey 三样。那样做有两个后果：每加一个 section 实例就要重填一遍
   （填漏一个就静默降级成发邮件），以及后台地址被印在页面上 ——
   主题就没法原样给别人用了。

   现在主题里只有**同域的相对路径** `/apps/forms/...`，由 Shopify 的
   App Proxy 转发到后台。剩下唯一需要知道的值是 Turnstile 的 sitekey，
   它由 `/apps/forms/config` 发下来。sitekey 本身是公开的（它必须出现
   在页面上才能渲染挂件），放到后台去发不是为了保密，是为了让主题
   保持通用：换一个店装上就能用，不用改一行代码。

   挂件是**显式渲染**的：api.js 自动渲染要求元素上先有 data-sitekey，
   而我们的 sitekey 要等一个网络往返才拿得到。所以这里自己调 render。

   拿不到配置时（proxy 没配好、Worker 挂了）：联系页和合作申请那两张
   退回 Shopify 原生联系表单发邮件 —— 它们本来就是 `{% form 'contact' %}`，
   退得回去，件不会丢，只是慢十分钟。保修和试骑那两张退不回去（它们的
   原生分支是服务端另一套 markup），只能让提交如实报错。
   ══════════════════════════════════════════════════════════════════ */
(function () {
  'use strict';

  /* 挂载点是商家能在后台改的，所以它是主题设置，由 theme.liquid 落进
     window.EBProxy。取不到就回落到默认 —— 这一件不该因为一个空设置就哑掉。 */
  var PROXY = (window.EBProxy && window.EBProxy.forms) || '/apps/forms';
  var CONFIG_URL = PROXY + '/config';
  var API_JS = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

  /* 两代表单用了两个不同的属性名（试骑那张是这套机制最早的样板，
     写在前面）。与其改一个正在跑的东西去迁就命名，不如两个都认。 */
  var SELECTOR = '[data-eb-turnstile], [data-tr-turnstile]';

  function widgets() {
    return Array.prototype.slice.call(document.querySelectorAll(SELECTOR));
  }

  /* 退回原生表单：只有真是 Shopify 联系表单的那两张退得回去，
     它们在 liquid 里标了 data-eb-native-ok。

     标记打在**外层容器**上，然后由这里找到里面的 form —— 给 Shopify 的
     `{% form %}` 标签加自定义 data-* 属性行为不可靠，而外层 div 是我们
     自己写的，加什么都行。提交件读的是 form 上的 dataset，所以标记要
     落到 form 身上。 */
  function fallbackToNative() {
    var marked = document.querySelectorAll('[data-eb-native-ok]');
    Array.prototype.forEach.call(marked, function (mount) {
      var form = mount.matches('form') ? mount : mount.querySelector('form');
      if (form) form.dataset.ebNative = 'true';
    });
    /* 挂件框留在那儿只会是一个永远转不出来的空位。 */
    widgets().forEach(function (box) { box.remove(); });
  }

  var apiLoading = null;
  function loadApi() {
    if (apiLoading) return apiLoading;
    apiLoading = new Promise(function (resolve, reject) {
      if (window.turnstile) return resolve();
      var s = document.createElement('script');
      s.src = API_JS;
      s.async = true;
      s.defer = true;
      s.onload = function () { resolve(); };
      s.onerror = function () { reject(new Error('turnstile api.js')); };
      document.head.appendChild(s);
    });
    return apiLoading;
  }

  function render(box, config) {
    /* 主题编辑器里换 section 会把同一个框再交给我们一次。
       渲染两次的结果是两个挂件叠在一起，第二个永远解不开。 */
    if (box.dataset.ebRendered === 'true') return;
    box.dataset.ebRendered = 'true';
    window.turnstile.render(box, {
      sitekey: config.sitekey,
      /* Worker 那边硬校验 action —— 对不上就是 403，而且前端看起来
         像是「验证失败，再试一次」，查起来很费劲。 */
      action: config.turnstileAction || undefined,
      appearance: 'interaction-only',
      theme: 'auto'
    });
  }

  var configPromise = null;
  function getConfig() {
    if (configPromise) return configPromise;
    configPromise = fetch(CONFIG_URL, {
      method: 'GET',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    })
      .then(function (r) {
        if (!r.ok) throw new Error(r.status);
        return r.json();
      })
      .then(function (data) {
        /* 回了 200 但没有 sitekey 一样算失败。没有 sitekey 就渲染不出
           挂件，而没有挂件的提交后台一定会拒 —— 那种「看起来能提交」
           是最坏的一种失败。 */
        if (!data || data.ok === false || !data.sitekey) throw new Error('no sitekey');
        return data;
      });
    return configPromise;
  }

  function init() {
    if (!widgets().length) return; /* 这一页没有需要验证的表单 */
    getConfig()
      .then(function (config) {
        return loadApi().then(function () {
          widgets().forEach(function (box) { render(box, config); });
        });
      })
      .catch(function () {
        fallbackToNative();
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  /* 主题编辑器里换 section 之后新的框要补渲染 */
  document.addEventListener('shopify:section:load', init);
})();
